import { defineComponent as _defineComponent } from 'vue'
import { ref, Ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useAuthStore, useFilterStore, useUiStore } from '@/stores';
import { IntegrationService, MetaIntegrationService } from '@/services';
import { toastServiceError } from '@/services/core/notification';

import { IntegrationWidget } from './components';
import { LevelModel, IntegrationModel, IntegrationType, MetaConnectionStatus } from '@/types';

import { LinkIcon } from '@heroicons/vue/solid';


export default /*@__PURE__*/_defineComponent({
  __name: 'IntegrationPage',
  setup(__props, { expose: __expose }) {
  __expose();

const router = useRouter();
const { t } = useI18n();
const filterStore = useFilterStore();
const uiStore = useUiStore();
const authStore = useAuthStore();
const hasWhatsAppMeta = computed(() => authStore.userModel?.hasWhatsAppMeta === true);

const integrations: Ref<IntegrationModel[]> = ref([]);
const whatsappMetaStatus: Ref<MetaConnectionStatus> = ref(null);

const whatsappMetaWidget = computed(() => ({
    logoPath: '/img/integration/whatsapp.svg',
    contentText: t('whatsappMeta.connect.description'),
    isActive:
        whatsappMetaStatus.value === null ? undefined : whatsappMetaStatus.value === 'connected',
}));

const integrationInfo = {
    [IntegrationType.Pipedrive]: {
        logoPath: '/img/integration/pipedrive.png',
        description: t('integration.integrations.pipedrive.description'),
    },
    [IntegrationType.OpenAi]: {
        logoPath: '/img/integration/openai.png',
        description: t('integration.integrations.openai.description'),
    },
    [IntegrationType.Moskit]: {
        logoPath: '/img/integration/moskit.png',
        description: t('integration.integrations.moskit.description'),
    },
};

const configuredIntegrations = computed<IntegrationModel[]>(() =>
    integrations.value.filter((integration) => integrationInfo[integration.type]),
);

const levelFilter = computed<LevelModel | null>(() => filterStore.level);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getWhatsAppMetaStatus = async () => {
    try {
        const { data } = await MetaIntegrationService.getConnectionStatus();
        whatsappMetaStatus.value = data.status;
    } catch (error) {
        toastServiceError(error);
    }
};

const getIntegrations = async () => {
    try {
        await setLoading(true);
        const response = await IntegrationService.getAll<Array<IntegrationModel>>();
        integrations.value = response.data;
        if (hasWhatsAppMeta.value) await getWhatsAppMetaStatus();
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const goToConnectWhatsAppMeta = () => router.push({ name: 'ListWhatsAppMeta' });

const handleConfigClick = (integration: IntegrationModel) => {
    router.push({
        name: 'EditIntegrationById',
        params: {
            type: integration.type,
            id: integration.id,
        },
    });
};

const handleViewLogsClick = (integration: IntegrationModel) => {
    router.push({
        name: 'ViewIntegrationLogs',
        params: {
            type: integration.type,
            id: integration.id,
        },
    });
};

const handleActivateClick = (integration: IntegrationModel) => {
    router.push({
        name: 'CreateIntegration',
        params: {
            type: integration.type,
        },
    });
};

watch([levelFilter], getIntegrations, { deep: true });

onMounted(getIntegrations);

const __returned__ = { router, t, filterStore, uiStore, authStore, hasWhatsAppMeta, integrations, whatsappMetaStatus, whatsappMetaWidget, integrationInfo, configuredIntegrations, levelFilter, setLoading, getWhatsAppMetaStatus, getIntegrations, goToConnectWhatsAppMeta, handleConfigClick, handleViewLogsClick, handleActivateClick, get IntegrationWidget() { return IntegrationWidget }, get LinkIcon() { return LinkIcon } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})